"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeroSection from "./components/DoralHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";

const neighborhood = "Doral";

export default function DoralPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const doralFaqs = [
    { question: t("hood.faq1Q"), answer: t("hood.faq1A") },
    { question: t("hood.faq2Q"), answer: t("hood.faq2A") },
    { question: t("hood.faq3Q"), answer: t("hood.faq3A") },
    { question: t("hood.faq4Q"), answer: t("hood.faq4A") },
    { question: t("doral.faq5Q"), answer: t("doral.faq5A") },
  ];

  useEffect(() => {
    document.title =
      currentLang === "es"
        ? "Reparaci\u00F3n de Ventanas Doral \u2014 Casas Modernas Miami"
        : "Doral Window Repair \u2014 Modern Home & New Construction";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      currentLang === "es"
        ? "Servicio de reparaci\u00F3n de ventanas en Doral. Especialistas en herrajes de casas modernas y construcci\u00F3n nueva. Miami-Dade tierra adentro con experiencia en calor y humedad."
        : "Doral window repair service. Modern home and new construction hardware specialists. Inland Miami-Dade with heat and humidity expertise.";

    const cleanupSeo = injectMetaTags({
      title: document.title,
      description: metaDesc.content,
      url: "https://impact-window-repair-miami.com/impact-windows-doral/",
      image: "https://impact-window-repair-miami.com/og/og-doral.jpg",
      imageAlt:
        currentLang === "es"
          ? "Reparaci\u00F3n de ventanas Doral Miami"
          : "Window repair Doral Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "\u00C1reas de Servicio" : "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: neighborhood, item: "https://impact-window-repair-miami.com/impact-windows-doral/" },
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Window Repair",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: {
            "@type": "City",
            name: "Doral, Miami-Dade, FL",
          },
          serviceType: "Window Repair",
          description:
            currentLang === "es"
              ? "Reparaci\u00F3n de herrajes, vidrios y mantenimiento en Doral."
              : "Hardware repair, glass replacement, and maintenance in Doral.",
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: doralFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      const existingFaq = document.getElementById("faq-schema");
      if (existingFaq) document.head.removeChild(existingFaq);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection
        h1={t("doral.h1")}
        subhead={t("doral.subhead")}
        zips={["33172", "33178", "33182", "33166"]}
      />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACI\u00D3N EN DORAL" : "DORAL WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/1bf1103f-6709-4c98-8c3c-acfcd699370c_Sliding-Door-Repair-Miami.jpg?v=caaec9e497d3fecc922adf6b92ec183e"
        imageAlt={currentLang === "es" ? "Especialista en reparaci\u00F3n de ventanas Doral Miami" : "Doral Miami window repair specialist servicing modern residential community"}
        heading={currentLang === "es" ? "Reparaci\u00F3n de Ventanas para Casas Modernas en Doral" : "Doral Window Repair for Modern Homes"}
        lead={t("doral.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reparaci\u00F3n de herrajes en marcos modernos con impacto",
                "Reemplazo de vidrios en ventanas secundarias no impacto",
                "Mantenimiento anual para casas de construcci\u00F3n nueva",
              ]
            : [
                "Hardware repair on modern impact frames",
                "Glass replacement in non-impact secondary windows",
                "Annual maintenance for new construction homes",
              ]
        }
        closing={
          currentLang === "es"
            ? "Las casas modernas de Doral a\u00FAn necesitan mantenimiento regular para preservar la vida de los herrajes."
            : "Doral modern homes still need regular maintenance to preserve hardware life."
        }
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection heading={t("hood.commonFaqH2")} items={doralFaqs} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: currentLang === "es" ? "Kendall" : "Kendall", slug: "kendall" },
        { name: currentLang === "es" ? "South Miami" : "South Miami", slug: "south-miami" },
        { name: currentLang === "es" ? "Pinecrest" : "Pinecrest", slug: "pinecrest" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}